import type { PlanificacionSemanalRow } from '@/types'
import { parseDate } from '@/utils/planningFormatters'
import { cuentaComoRotura } from '@/utils/estadoMaterial'

/**
 * Cuándo rompe un componente.
 *
 * El Gantt sabía decir qué ÓRDENES tienen rotura; no sabía decir **qué día** se
 * queda sin material un componente concreto. El dato estaba: cada fila de plan
 * es (orden × componente) y lleva su propio saldo. Aquí se le da forma de
 * cronología.
 *
 * ── Lo que se midió en el origen (`dbo.tabla_planificacion`, 4.957 filas) ────
 *
 * 1. `disponibleAntes = stockInicial + entradasHastaOrden − consumoPrevio`
 *    se cumple en las 4.957. Cada fila se explica sola: **no hay que encadenar
 *    nada**. Y menos mal, porque `ordenFinal` EMPATA entre las filas de una
 *    misma orden y encadenar por él daría un saldo distinto según cómo caiga el
 *    desempate.
 *
 * 2. `disponibleDespues = disponibleAntes − pendiente`, también en las 4.957.
 *    El saldo baja por lo PENDIENTE, no por el consumo estimado: lo que ya está
 *    reservado está apartado y no se lo puede llevar otro. Por eso una orden que
 *    consume 4.000 unidades puede dejar el saldo intacto.
 *
 * 3. `flagRotura` **no se recalcula**. Las 1.037 filas marcadas cumplen
 *    `pendiente > disponibleAntes`, pero hay 37 que lo cumplen y NO están
 *    marcadas. El notebook sabe algo que la comparación no, así que la marca se
 *    usa tal cual viene y la comparación sólo sirve para decir cuánto falta.
 *
 * ── El grano, que no es el que parece ───────────────────────────────────────
 *
 * El saldo es por **empresa × artículo × almacén × color × estilo**. Los dos
 * últimos no son decoración: medido sobre el año entero, con esas cinco claves
 * el `stockInicial` es único en los 1.622 grupos, **sin una sola excepción**; si
 * se quitan el color y el estilo quedan 1.529 grupos y 42 con varios stocks
 * dentro, es decir, 42 series que mezclarían variantes distintas.
 *
 * Caso real medido: `M003009` en GAVA tiene cuatro variantes con 36.765, 1.162,
 * 154.500 y **0** unidades. La que rompe es la de 0; agrupando por el código a
 * secas, la franja habría pintado una rotura en mitad de un saldo de 154.500 y
 * nadie habría entendido por qué.
 */

/** Una orden llevándose material: un escalón del saldo. */
export interface ConsumoComponente {
  prodid: string
  /** Cuándo se lo lleva. `null` si la orden no tiene fecha de inicio: no se
   *  puede colocar en el eje, pero sigue contando para el recuento. */
  fecha: Date | null
  /** Lo que la orden necesita. */
  consumo: number
  /** La parte NO reservada, que es la que mueve el saldo. */
  pendiente: number
  disponibleAntes: number
  disponibleDespues: number
  /** Tal cual lo marca el origen. Ver el punto 3 de la cabecera. */
  rotura: boolean
  /** Unidades que faltan en este escalón. 0 cuando cubre. */
  faltan: number
}

export interface SerieComponente {
  /** `empresa|itemid|almacén|color|estilo`. Estable entre refrescos: sirve de
   *  `key`, y declara el grano completo del saldo. */
  clave: string
  empresa: string
  itemid: string
  nombre?: string
  almacen: string
  /** La variante. Va en la serie porque va en el grano: dos variantes del mismo
   *  código son dos stocks distintos y hay que poder distinguirlas en pantalla. */
  color?: string
  estilo?: string
  stockInicial: number
  /** En orden de calendario; las órdenes sin fecha, al final. */
  consumos: ConsumoComponente[]
  /** El primer escalón marcado con rotura: la respuesta a «¿cuándo?». */
  primeraRotura: ConsumoComponente | null
  /** Cuántos escalones rompen, en total. */
  totalRoturas: number
  /** Escalones que no se pueden colocar en el eje por no tener fecha. */
  sinFecha: number
  /** La entrada de compra que cubre el pendiente, cuando el origen la conoce.
   *  Es la otra mitad de la respuesta: cuándo deja de romper. */
  entradaCubre: { fecha: Date; cantidad: number | null } | null
}

const num = (v: number | undefined | null): number => (typeof v === 'number' && isFinite(v) ? v : 0)

/**
 * Las series de los componentes que casan con `casa`, una por
 * empresa × artículo × almacén × color × estilo.
 *
 * **Se le pasan TODAS las filas del rango, no sólo las de las órdenes
 * visibles.** El stock se lo lleva quien se lo lleva: si se contaran sólo las
 * órdenes que el resto de filtros deja ver, la fecha de rotura saldría más
 * tarde de lo que es, que es la peor forma de equivocarse aquí.
 *
 * Las series salen ordenadas por lo que más corre prisa: primero las que
 * rompen, y entre ellas la que rompe antes.
 */
export function seriesDeComponente(
  filas: PlanificacionSemanalRow[],
  casa: (r: PlanificacionSemanalRow) => boolean,
): SerieComponente[] {
  const porClave = new Map<string, PlanificacionSemanalRow[]>()
  for (const r of filas) {
    if (!casa(r)) continue
    const clave = [
      r.dataareaid, r.itemid, r.inventlocationid ?? '',
      r.inventcolorid ?? '', r.inventstyleid ?? '',
    ].join('|')
    const l = porClave.get(clave) ?? []
    l.push(r)
    porClave.set(clave, l)
  }

  const series: SerieComponente[] = []
  for (const [clave, grupo] of porClave) {
    const ordenadas = [...grupo].sort((a, b) => {
      const fa = parseDate(a.fechaInicio)?.getTime()
      const fb = parseDate(b.fechaInicio)?.getTime()
      // Las sin fecha, al final: no se pueden colocar, pero no se tiran.
      if (fa === undefined && fb === undefined) return num(a.ordenFinal) - num(b.ordenFinal)
      if (fa === undefined) return 1
      if (fb === undefined) return -1
      return fa - fb || num(a.ordenFinal) - num(b.ordenFinal)
    })

    const consumos: ConsumoComponente[] = ordenadas.map(r => {
      const disponibleAntes = num(r.disponibleAntes)
      const pendiente = num(r.pendiente)
      return {
        prodid: r.prodid,
        fecha: parseDate(r.fechaInicio),
        consumo: num(r.consumoEstimado),
        pendiente,
        disponibleAntes,
        disponibleDespues: num(r.disponibleDespues),
        rotura: cuentaComoRotura(r),
        faltan: Math.max(0, pendiente - disponibleAntes),
      }
    })

    const primera = ordenadas.find(r => cuentaComoRotura(r))
    const fechaEntrada = primera ? parseDate(primera.fechaProximaEntradaCubre) : null

    series.push({
      clave,
      empresa: grupo[0].dataareaid,
      itemid: grupo[0].itemid,
      nombre: grupo.find(r => r.nombreMaterial)?.nombreMaterial,
      almacen: grupo[0].inventlocationid ?? '',
      color: grupo[0].inventcolorid || undefined,
      estilo: grupo[0].inventstyleid || undefined,
      stockInicial: num(grupo[0].stockInicial),
      consumos,
      primeraRotura: consumos.find(c => c.rotura) ?? null,
      totalRoturas: consumos.filter(c => c.rotura).length,
      sinFecha: consumos.filter(c => c.fecha === null).length,
      entradaCubre: fechaEntrada
        ? { fecha: fechaEntrada, cantidad: primera?.qtyProximaEntradaCubre ?? null }
        : null,
    })
  }

  return series.sort((a, b) => {
    const ra = a.primeraRotura?.fecha?.getTime()
    const rb = b.primeraRotura?.fecha?.getTime()
    // Las que rompen, primero; entre ellas, la que rompe antes.
    if (ra !== undefined && rb !== undefined && ra !== rb) return ra - rb
    if (a.totalRoturas !== b.totalRoturas) return b.totalRoturas - a.totalRoturas
    return a.itemid.localeCompare(b.itemid) || a.clave.localeCompare(b.clave)
  })
}

/** Un tramo de la franja: desde dónde hasta dónde, y en qué estado. */
export interface TramoCobertura {
  desde: Date
  /** `null` = hasta el final del rango visible. */
  hasta: Date | null
  /** El tramo está en rotura: lo dice el escalón que lo abre. */
  rotura: boolean
  /** La orden que abrió el tramo. `null` en el tramo inicial, el que va desde el
   *  principio del rango hasta el primer consumo. */
  prodid: string | null
  /** El saldo durante el tramo. */
  saldo: number
}

/**
 * La serie convertida en tramos de color para pintar.
 *
 * Cada consumo abre un tramo que dura hasta el siguiente, y el color del tramo
 * es el estado del escalón que lo abre: así **todo color sale de un dato**, no
 * de una interpolación. El tramo inicial —antes del primer consumo— es el stock
 * de partida, y se pinta cubierto porque todavía no se ha llevado nadie nada.
 *
 * Si una orden posterior deja de romper porque llegó una compra, su tramo sale
 * cubierto: la franja enseña la recuperación sin que haya que programarla.
 *
 * Varios consumos del mismo día colapsan en un tramo: en la vista anual una
 * columna es un mes entero y pintar treinta tramos de 0,1 % no se ve.
 */
export function tramosDeSerie(serie: SerieComponente, desdeRango: Date): TramoCobertura[] {
  const conFecha = serie.consumos.filter((c): c is ConsumoComponente & { fecha: Date } => c.fecha !== null)
  if (conFecha.length === 0) return []

  const tramos: TramoCobertura[] = []
  const primero = conFecha[0].fecha

  if (primero.getTime() > desdeRango.getTime()) {
    tramos.push({
      desde: desdeRango, hasta: primero, rotura: false, prodid: null, saldo: serie.stockInicial,
    })
  }

  for (let i = 0; i < conFecha.length; i++) {
    const c = conFecha[i]
    const siguiente = conFecha[i + 1]?.fecha ?? null
    // Mismo día que el siguiente: no abre tramo propio, pero su rotura sí tiene
    // que llegar al tramo del día — si no, un día con dos órdenes podría perder
    // la roja por el orden en que vinieron.
    if (siguiente && siguiente.getTime() === c.fecha.getTime()) {
      const resto = conFecha.slice(i + 1).findIndex(x => x.fecha.getTime() !== c.fecha.getTime())
      const ultimoDelDia = resto === -1 ? conFecha.length - 1 : i + resto
      const delDia = conFecha.slice(i, ultimoDelDia + 1)
      tramos.push({
        desde: c.fecha,
        hasta: conFecha[ultimoDelDia + 1]?.fecha ?? null,
        rotura: delDia.some(x => x.rotura),
        prodid: (delDia.find(x => x.rotura) ?? delDia[delDia.length - 1]).prodid,
        saldo: delDia[delDia.length - 1].disponibleDespues,
      })
      i = ultimoDelDia
      continue
    }
    tramos.push({
      desde: c.fecha, hasta: siguiente, rotura: c.rotura, prodid: c.prodid, saldo: c.disponibleDespues,
    })
  }

  return tramos
}
